/**
 * Auto-reload utility for invisible page refresh after CRUD operations
 * Triggers a silent data refresh without full page reload
 */

/**
 * Triggers an invisible auto-reload by dispatching a custom event
 * Components can listen to this event to refresh their data
 * @param {string} context - Context of the reload (e.g., 'product-created', 'product-updated')
 */
export const triggerAutoReload = (context = 'data-changed') => {
  window.dispatchEvent(new CustomEvent('auto-reload', {
    detail: { context, timestamp: Date.now() }
  }));
};

/**
 * Hook to listen for auto-reload events
 * @param {Function} callback - Function to call when auto-reload is triggered
 * @param {Array} dependencies - Dependencies for the effect
 */
export const useAutoReload = (callback, dependencies = []) => {
  if (typeof window === 'undefined') return;

  const handleAutoReload = (event) => {
    console.log('Auto-reload triggered:', event?.detail);
    callback(event?.detail);
  };

  window.addEventListener('auto-reload', handleAutoReload);

  return () => {
    window.removeEventListener('auto-reload', handleAutoReload);
  };
};

/**
 * Wraps a CRUD operation with auto-reload trigger
 * @param {Function} operation - The async operation to execute
 * @param {string} context - Context for the reload event
 */
export const withAutoReload = async (operation, context) => {
  try {
    const result = await operation();
    triggerAutoReload(context);
    return result;
  } catch (error) {
    console.error('Operation failed:', error);
    throw error;
  }
};
